/* eslint-disable no-console */
import _get from 'lodash/get';
import _merge from 'lodash/merge';
import Store from 'lightningjs-redux';
import { Lightning, Router } from '@lightningjs/sdk';
import { imageService, LAYOUT_ENTITIES } from '@bedrock/fido-sdk';
import { DEFAULT_IMAGE_FORMAT, IMAGE_FIT_SCALE_CROP, IMAGE_QUALITY } from '@bedrock-rdk/constants';
import {
  PLAYBACK_STATE_ENDED,
  PLAYBACK_STATE_PAUSED,
  PLAYBACK_STATE_PLAYING,
  reset,
} from '../../../../../logic/player/player.constants';
import { showSnackbar } from '../../../../app/utils/app.utils';
import { NOTIFICATION_LEVEL } from '../../../../tornado/notifications';
import { navigateToAudioPage, navigateToRadioPage } from '../../../../routes/routes.utils';
import { destroyAudioPlayer, getAudioPlayerInstance, whenAudioPlayerReady } from '../../../../audio/audio.adapter';
import { Autoscroll } from '../../../../audio/autoscroll/autoscroll.component';
import { Bars } from '../../spectrum/bars.component';
import connect from '../../miniPlayer.connect';
import {
  COVER_SIZE,
  defaultContext,
  GAP,
  getFocusedStyle,
  getInitialStyle,
  getUnFocusedStyle,
  PADDING,
  TRANSITION_CONFIG,
} from '../../miniPlayer.style';
import { HIDE_SIGNAL } from '../../miniPlayer.constants';

export class MiniPlayer extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {
          rect: true,
        },
        Spectrum: {
          type: Bars,
        },
        Cover: {},
        ExtraTitle: {
          type: Autoscroll,
        },
        Title: {
          type: Autoscroll,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
    this._isRadio = false;
    this._requestedSource = null;
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }

  get isActive() {
    return this._playbackState === PLAYBACK_STATE_PLAYING || this._playbackState === PLAYBACK_STATE_PAUSED;
  }

  set metadata(value) {
    this._clipId = _get(value, 'video.id');
    const extraTitle = _get(value, 'extraTitle') || '';
    const title = _get(value, 'title') || '';
    const image = _get(value, 'image.idsByRatio.1:1') || _get(value, 'image.id') || '';
    const { screenSize } = this._style;
    const coverSize = COVER_SIZE[screenSize];

    this.patch({
      Cover: {
        src: imageService.getUrl(image, coverSize, coverSize, {
          fit: IMAGE_FIT_SCALE_CROP,
          quality: IMAGE_QUALITY,
          format: DEFAULT_IMAGE_FORMAT,
        }),
      },
      ExtraTitle: {
        caption: extraTitle,
      },
      Title: {
        caption: title,
      },
    });

    if (image) {
      this.tag('Cover').loadTexture();
    }
  }

  set source(value) {
    this._requestedSource = value;

    const audioPlayer = getAudioPlayerInstance();
    if (!audioPlayer) {
      // NOTE: the radio engine is loaded on demand, the source is applied once it is in place unless a newer one came
      whenAudioPlayerReady().then(player => {
        if (player && this._requestedSource === value) {
          this._applySource(player, value);
        }
      });

      return;
    }

    this._applySource(audioPlayer, value);
  }

  _applySource(audioPlayer, value) {
    if (!value) {
      audioPlayer.pause();
      this.tag('Spectrum').stop();

      return;
    }

    this._isRadio = value.provider === 'radio';

    if (audioPlayer.source && audioPlayer.source.reference === value.reference) {
      console.log(`[MiniPlayer] Current source is the same: ${value.reference}`);
      audioPlayer.play();

      return;
    }

    audioPlayer.pause();

    if (value.format === 'mp3' || value.format === 'hls') {
      console.log(`[MiniPlayer] Requesting a new playback of: ${value.reference}`);

      audioPlayer.setSource(value);
      audioPlayer.play();
    } else {
      console.log('[MiniPlayer] Destroying audio player');
      // video playback was requested, stopping audio player and forgetting what it was playing
      this._destroyAudioPlayer();
    }
  }

  set playbackError(value) {
    const audioPlayer = getAudioPlayerInstance();
    if (!value || !audioPlayer) {
      return;
    }

    if (!this.isOnAudioPlayerScreen) {
      showSnackbar(this, NOTIFICATION_LEVEL.ERROR, value.message);
    }

    this.tag('Spectrum').stop();
    this._destroyAudioPlayer();
    Store.dispatch(reset());
  }

  set playbackState(value) {
    // The source was removed (i.e. player was reset) however a play or pause event was dispatched after that
    if (!this._clipId && (value === PLAYBACK_STATE_PLAYING || value === PLAYBACK_STATE_PAUSED)) {
      this.tag('Spectrum').stop();

      return;
    }

    this._playbackState = value;

    switch (value) {
      case PLAYBACK_STATE_PLAYING: {
        this.tag('Spectrum').start();
        break;
      }
      case PLAYBACK_STATE_PAUSED: {
        this.tag('Spectrum').stop(true);
        break;
      }
      case PLAYBACK_STATE_ENDED: {
        this.tag('Spectrum').stop();
        if (this.isOnAudioPlayerScreen) {
          Router.back();

          this._destroyAudioPlayer();
          Store.dispatch(reset());
        }
        break;
      }
      default: {
        this.tag('Spectrum').stop();
        break;
      }
    }

    const isVisible = this._playbackState === PLAYBACK_STATE_PLAYING || this._playbackState === PLAYBACK_STATE_PAUSED;
    if (!isVisible) {
      this.signal(HIDE_SIGNAL);
    }
    this.visible = isVisible;
  }

  get isOnAudioPlayerScreen() {
    const route = Router.getActiveRoute();

    return route.startsWith(LAYOUT_ENTITIES.audio) || route.startsWith(LAYOUT_ENTITIES.live);
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(getUnFocusedStyle(this._style));
  }

  _handleEnter() {
    if (!this._clipId) {
      return false;
    }

    if (this._isRadio) {
      navigateToRadioPage(this._clipId);
    } else {
      navigateToAudioPage(this._clipId);
    }

    return true;
  }

  _handlePlay() {
    if (this._playbackState === PLAYBACK_STATE_PLAYING) {
      getAudioPlayerInstance().pause();

      return true;
    }

    return false;
  }

  _handlePause() {
    if (this._playbackState === PLAYBACK_STATE_PAUSED) {
      getAudioPlayerInstance().play();

      return true;
    }

    return false;
  }

  _handlePlayPause() {
    return this._handlePlay() || this._handlePause();
  }

  _destroyAudioPlayer() {
    destroyAudioPlayer();
  }

  open(width) {
    const { screenSize } = this._style;
    const barsWidth = this.tag('Spectrum').w;
    const textWidth = width - COVER_SIZE[screenSize] - PADDING[screenSize] * 3 - GAP[screenSize] - barsWidth;

    this.patch({
      Background: {
        smooth: {
          alpha: [
            this._playbackState === PLAYBACK_STATE_PLAYING || this._playbackState === PLAYBACK_STATE_PAUSED ? 1 : 0,
            TRANSITION_CONFIG,
          ],
        },
      },
      Spectrum: {
        smooth: {
          x: [width - PADDING[screenSize] - barsWidth, TRANSITION_CONFIG],
        },
      },
      Cover: {
        smooth: {
          alpha: [1, TRANSITION_CONFIG],
        },
      },
      ExtraTitle: {
        w: textWidth,
        smooth: {
          alpha: 1,
        },
      },
      Title: {
        w: textWidth,
        smooth: {
          alpha: 1,
        },
      },
    });
  }

  close(width) {
    this.patch({
      Background: {
        smooth: {
          alpha: [0, TRANSITION_CONFIG],
        },
      },
      Spectrum: {
        smooth: {
          x: [Math.round((width - this.tag('Spectrum').w) / 2), TRANSITION_CONFIG],
        },
      },
      Cover: {
        smooth: {
          alpha: [0, TRANSITION_CONFIG],
        },
      },
      ExtraTitle: {
        smooth: {
          alpha: 0,
        },
      },
      Title: {
        smooth: {
          alpha: 0,
        },
      },
    });
  }
}
